import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Facebook, Heart, MessageCircle, Send, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl } from "../api";
import { PublicHeader } from "../components/PublicHeader";
import { AdSlot } from "../components/AdSlot";

export default function ArticlePage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [comment, setComment] = useState({ name: "", email: "", body: "" });

  const loadArticle = async () => {
    try {
      const response = await api.get(`/posts/${slug}`);
      setPost(response.data);
      const commentsRes = await api.get(`/posts/${response.data.id}/comments`);
      setComments(commentsRes.data);
    } catch (error) {
      toast.error("செய்தி கிடைக்கவில்லை");
    }
  };

  useEffect(() => { loadArticle(); }, [slug]);

  const likePost = async () => {
    const response = await api.post(`/posts/${post.id}/like`);
    setPost({ ...post, likes: response.data.likes });
  };

  const submitComment = async (event) => {
    event.preventDefault();
    try {
      await api.post(`/posts/${post.id}/comments`, comment);
      setComment({ name: "", email: "", body: "" });
      toast.success("கருத்து அனுப்பப்பட்டது. Admin approvalக்கு பிறகு வெளியாகும்.");
    } catch (error) {
      toast.error("கருத்தை அனுப்ப முடியவில்லை");
    }
  };

  if (!post) {
    return <div data-testid="article-loading-page"><PublicHeader /><div className="vtc-container py-16 font-bold text-slate-700" data-testid="article-loading-text">செய்தி ஏற்றப்படுகிறது...</div></div>;
  }

  const pageUrl = window.location.href;
  const hero = mediaUrl(post.hero_media?.url);
  const shareText = encodeURIComponent(post.title);
  const encodedUrl = encodeURIComponent(pageUrl);

  return (
    <div data-testid="article-page">
      <PublicHeader />
      <main className="vtc-container py-8 sm:py-10">
        <Link to="/" className="text-sm font-black text-blue-900 underline-offset-4 hover:underline" data-testid="article-back-link">← முகப்பு</Link>
        <article className="mt-6 grid gap-8 lg:grid-cols-[1fr_320px]" data-testid="article-layout">
          <div data-testid="article-main-column">
            <div className="mb-5 flex flex-wrap items-center gap-3" data-testid="article-meta-row">
              <span className="bg-green-800 px-3 py-1 text-xs font-black uppercase tracking-[0.16em] text-white" data-testid="article-category">{post.category_tamil_name}</span>
              <span className="text-sm font-bold text-slate-500" data-testid="article-date">{new Date(post.created_at).toLocaleDateString("ta-IN")}</span>
            </div>
            <h1 className="text-4xl font-black leading-tight text-slate-950 sm:text-5xl lg:text-6xl" data-testid="article-title">{post.title}</h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-600" data-testid="article-excerpt">{post.excerpt}</p>
            <div className="mt-8 overflow-hidden border border-slate-200 bg-white" data-testid="article-hero-media">
              {hero ? (
                post.hero_media?.media_type === "video" ? <video src={hero} controls className="aspect-video w-full bg-slate-950" data-testid="article-hero-video" /> : <img src={hero} alt={post.title} className="aspect-[16/9] w-full object-cover" data-testid="article-hero-image" />
              ) : <div className="grid aspect-[16/9] place-items-center bg-blue-50 font-black text-blue-900" data-testid="article-no-image">V.T.C Tamil.com</div>}
            </div>
            <div className="my-8"><AdSlot size="wide" label="In-Article Advertisement" /></div>
            <div className="prose prose-slate max-w-none whitespace-pre-line text-lg leading-loose text-slate-800" data-testid="article-body">{post.body}</div>
            {post.media?.filter((item) => item.id !== post.hero_media?.id).length > 0 && (
              <div className="mt-8 grid gap-4 sm:grid-cols-2" data-testid="article-gallery">
                {post.media.filter((item) => item.id !== post.hero_media?.id).map((item) => item.media_type === "video" ? <video key={item.id} src={mediaUrl(item.url)} controls className="w-full border border-slate-200 bg-slate-950" data-testid={`article-gallery-video-${item.id}`} /> : <img key={item.id} src={mediaUrl(item.url)} alt={item.original_filename} className="aspect-[4/3] w-full border border-slate-200 object-cover" data-testid={`article-gallery-image-${item.id}`} />)}
              </div>
            )}
            <div className="mt-8 flex flex-wrap gap-3 border-y border-slate-200 py-5" data-testid="article-actions">
              <button onClick={likePost} className="inline-flex items-center gap-2 bg-blue-900 px-4 py-2 font-black text-white transition-colors duration-200 hover:bg-blue-800" data-testid="article-like-button"><Heart className="h-5 w-5" aria-hidden="true" />{post.likes}</button>
              <a href={`https://wa.me/?text=${shareText}%20${encodedUrl}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-green-700 px-4 py-2 font-black text-green-800 transition-colors duration-200 hover:bg-green-800 hover:text-white" data-testid="article-share-whatsapp"><Send className="h-5 w-5" aria-hidden="true" />WhatsApp</a>
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-blue-900 px-4 py-2 font-black text-blue-900 transition-colors duration-200 hover:bg-blue-900 hover:text-white" data-testid="article-share-facebook"><Facebook className="h-5 w-5" aria-hidden="true" />Facebook</a>
              <a href={`https://twitter.com/intent/tweet?text=${shareText}&url=${encodedUrl}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-slate-800 px-4 py-2 font-black text-slate-800 transition-colors duration-200 hover:bg-slate-900 hover:text-white" data-testid="article-share-x"><Share2 className="h-5 w-5" aria-hidden="true" />X</a>
            </div>

            <section className="mt-10" data-testid="comments-section">
              <h2 className="text-3xl font-black text-slate-950" data-testid="comments-title">கருத்துகள்</h2>
              <form onSubmit={submitComment} className="mt-5 grid gap-3 border border-slate-200 bg-white p-5" data-testid="comment-form">
                <input value={comment.name} onChange={(e) => setComment({ ...comment, name: e.target.value })} required placeholder="பெயர்" className="border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-900" data-testid="comment-name-input" />
                <input value={comment.email} onChange={(e) => setComment({ ...comment, email: e.target.value })} required type="email" placeholder="மின்னஞ்சல்" className="border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-900" data-testid="comment-email-input" />
                <textarea value={comment.body} onChange={(e) => setComment({ ...comment, body: e.target.value })} required placeholder="உங்கள் கருத்து" rows="4" className="border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-900" data-testid="comment-body-input" />
                <button type="submit" className="inline-flex w-fit items-center gap-2 bg-green-800 px-5 py-3 font-black text-white transition-colors duration-200 hover:bg-green-700" data-testid="comment-submit-button"><MessageCircle className="h-5 w-5" aria-hidden="true" />கருத்து அனுப்பு</button>
              </form>
              <div className="mt-5 space-y-4" data-testid="approved-comments-list">
                {comments.map((item) => <div key={item.id} className="border border-slate-200 bg-white p-4" data-testid={`approved-comment-${item.id}`}><p className="font-black text-slate-950" data-testid={`approved-comment-name-${item.id}`}>{item.name}</p><p className="mt-2 leading-relaxed text-slate-700" data-testid={`approved-comment-body-${item.id}`}>{item.body}</p></div>)}
              </div>
            </section>
          </div>
          <aside className="space-y-6" data-testid="article-sidebar">
            <AdSlot size="sidebar" label="Article Sidebar Advertisement" />
            <AdSlot size="square" label="Square Advertisement" />
          </aside>
        </article>
      </main>
    </div>
  );
}